/*
 * From https://www.redblobgames.com/x/2237-painting-biomes/
 * Copyright 2022 Red Blob Games <redblobgames@gmail.com>
 * License: Apache-2.0 <http://www.apache.org/licenses/LICENSE-2.0.html>
 */

import SimplexNoise from 'simplex-noise';

const CANVAS_SIZE = 128;

type Point = {x: number, y: number};

function clamp(x: number, lo: number, hi: number): number { return x < lo ? lo : x > hi ? hi : x; }

function canvasCoords(canvas: HTMLCanvasElement, point: Point) {
    let rect = canvas.getBoundingClientRect();
    let x = (point.x - rect.left) / rect.width * canvas.width;
    let y = (point.y - rect.top) / rect.height * canvas.height;
    return {x, y, inRange: 0 <= x && x < canvas.width && 0 <= y && y < canvas.height};
}

function makePaintable(id: string, callback) {
    const canvas = document.getElementById(id) as HTMLCanvasElement;
    canvas.width = CANVAS_SIZE;
    canvas.height = CANVAS_SIZE;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    const R = 15;
    const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, R);
    gradient.addColorStop(0.1, "rgb(255, 255, 255, 1.0)");
    gradient.addColorStop(0.5, "rgb(0, 0, 0, 0.3)");
    gradient.addColorStop(1.0, "rgb(0, 0, 0, 0.1)");
    ctx.fillStyle = gradient;
    ctx.globalCompositeOperation = 'lighten';
    
    let dragging = false;

    function down(event) {
        if (event.button !== 0) return; // only capture main button, not right click
        dragging = true;
        move(event);
        canvas.setPointerCapture(event.pointerId);
    }
    function up(event) {
        move(event);
        dragging = false;
    }
    function move(event) {
        if (!dragging) return;
        event.preventDefault();
        event.stopPropagation();
        let {x, y, inRange} = canvasCoords(canvas, event);

        ctx.translate(x, y);
        ctx.fillRect(-R, -R, 2*R, 2*R);
        ctx.translate(-x, -y);
        callback();
    }
    
    canvas.addEventListener('pointerdown', down);
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointermove', move);
    
    return ctx;
}

let elevation = makePaintable('in-elevation', redraw);
let moisture = makePaintable('in-moisture', redraw);

function redraw() {
    const e_in = elevation.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE).data;
    const m_in = moisture.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE).data;
    const canvas = document.getElementById('out-biome') as HTMLCanvasElement;
    canvas.width = CANVAS_SIZE;
    canvas.height = CANVAS_SIZE;
    const biome_out = canvas.getContext('2d');
    const imageData = biome_out.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    const pixels = imageData.data;

    for (let x = 0; x < CANVAS_SIZE; x++) {
        for (let y = 0; y < CANVAS_SIZE; y++) {
            let start = 4 * (y * CANVAS_SIZE + x);
            let e = e_in[start] / 255;
            let m = m_in[start] / 255;

            /* magic formula from https://www.redblobgames.com/x/1446-svg-filters/ */
            let r = 0.824 + 0.6 * e - 0.8 * m;
            let g = 0.726 + 0.4 * e - 0.57 * m;
            let b = 0.545 + 0.4 * e - 0.6 * m;
            if (e < 0.1) { r = 0.1; g = 0.2; b = 0.6; }
            pixels[start + 0] = clamp((255*r) | 0, 0, 255);
            pixels[start + 1] = clamp((255*g) | 0, 0, 255);
            pixels[start + 2] = clamp((255*b) | 0, 0, 255);
            pixels[start + 3] = 255;
        }
    }

    biome_out.putImageData(imageData, 0, 0);
}

redraw();
